// src/pages/InstantAssessment.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAssessment } from '../contexts/AssessmentContext';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  MenuItem
} from '@mui/material';

import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  Terminal as CoreIcon,
  Apps as AppAIIcon,
  Person as UserAIIcon,
  DesignServices as DesignIcon,
  DeveloperMode as DevIcon,
  RocketLaunch as DeployIcon
} from '@mui/icons-material';

export default function InstantAssessment() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    org_name: '',
    contact_email: '',
    country: '',
    company_type: '',
    current_stage: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const countries = [
    "United States", "United Kingdom", "Canada", "Australia", "Germany",
    "France", "Netherlands", "Switzerland", "Sweden", "Denmark", "Norway",
    "Singapore", "Japan", "South Korea", "India", "China", "Brazil",
    "Mexico", "Argentina", "South Africa", "UAE", "Other"
  ];

  // FULL descriptions provided by you
  const companyTypeInfo = [
    {
      key: "Core AI",
      icon: <CoreIcon sx={{ fontSize: 40 }} />,
      title: "Core AI Companies",
      desc: `Core AI companies focus on developing the foundational technologies and
infrastructure that power artificial intelligence.`
    },
    {
      key: "Application AI",
      icon: <AppAIIcon sx={{ fontSize: 40 }} />,
      title: "Application AI Companies",
      desc: `Application AI companies focus on developing AI-driven products and
solutions that apply across various industries.`
    },
    {
      key: "User AI",
      icon: <UserAIIcon sx={{ fontSize: 40 }} />,
      title: "User AI Companies",
      desc: `User AI companies focus on developing AI-driven technologies and applications
that directly interact with end users.`
    }
  ];

  const stageInfo = [
    {
      key: "Design",
      icon: <DesignIcon sx={{ fontSize: 40 }} />,
      title: "Design Stage",
      desc: `An organization enters the design phase when it identifies and formulates the problem, and
explores, collects, and annotates data.`
    },
    {
      key: "Development",
      icon: <DevIcon sx={{ fontSize: 40 }} />,
      title: "Development Stage",
      desc: `An organization operates in the development phase during data preprocessing, model
training, testing, and evaluation.`
    },
    {
      key: "Deployment",
      icon: <DeployIcon sx={{ fontSize: 40 }} />,
      title: "Deployment Stage",
      desc: `An organization reaches the deployment phase when it operationalises the AI system,
integrates it into production environments, and subjects it to continuous monitoring, evaluation, and
oversight.`
    }
  ];

  const steps = [
    'Organization Details',
    'Instructions',
    'Assessment Questions',
    'Review & Submit',
    'Results'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const validateForm = () => {
    const required = ['org_name', 'contact_email', 'country', 'company_type', 'current_stage'];
    const missing = required.filter(field => !formData[field]);

    if (missing.length > 0) {
      setError('Please fill in all required fields');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.contact_email)) {
      setError('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`https://py.taih.ai/api/assessments/instant/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        mode: 'cors',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        sessionStorage.setItem('sessionToken', data.session_token);
        sessionStorage.setItem('assessmentId', data.assessment_id.toString());
        sessionStorage.setItem('accessLink', data.access_link);

        navigate('/assessment/instructions', {
          state: {
            sessionToken: data.session_token,
            assessmentId: data.assessment_id
          }
        });
      } else {
        const err = await response.json();
        setError(err.detail ?? 'Failed to start assessment.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // 🔥 FINAL WORKING BOX COMPONENT
  const selectionBox = (item, selectedValue, fieldName) => (
    <Box
      key={item.key}
      onClick={() => setFormData(prev => ({ ...prev, [fieldName]: item.key }))}
      sx={{
        cursor: "pointer",
        p: 3,
        borderRadius: 3,
        height: "100%",
        border: selectedValue === item.key ? "2px solid #1976d2" : "1px solid #ccc",
        backgroundColor: selectedValue === item.key ? "#e8f1ff" : "white",
        boxShadow: selectedValue === item.key ? 4 : 1,
        transition: "0.25s",
        "&:hover": {
          boxShadow: 4,
          transform: "scale(1.03)"
        },
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start"
      }}
    >
      <Box sx={{ textAlign: "center" }}>
        {item.icon}
        <Typography variant="h6" sx={{ mt: 1, fontWeight: 600 }}>
          {item.title}
        </Typography>
      </Box>

      <Typography
        variant="body2"
        sx={{
          mt: 2,
          whiteSpace: "pre-line",
          color: "text.secondary",
          textAlign: "center"
        }}
      >
        {item.desc}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>

      {/* HEADER */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 2 }}>
        <Container>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
            <IconButton
              onClick={() => navigate('/')}
              sx={{ color: 'white', position: 'absolute', left: 0 }}
            >
              <ArrowBackIcon />
            </IconButton>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <BusinessIcon sx={{ mr: 2, fontSize: 28 }} />
              <Typography variant="h5" sx={{ fontWeight: 600,color: 'white' }}>
                RAISE Index - Instant Assessment
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Stepper activeStep={0} alternativeLabel>
              {steps.map((label, idx) => (
                <Step key={label}>
                  <StepLabel
                    sx={{
                      '& .MuiStepLabel-label': { color: 'white !important' },
                      '& .MuiStepIcon-root': {
                        color: idx === 0 ? '#fff !important' : undefined
                      },
                      '& .MuiStepIcon-text': {
                        fill: idx === 0 ? '#00438f' : undefined
                      }
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Container>
      </Box>

      {/* FORM AREA */}
      <Container sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, maxWidth: 900, mx: 'auto' }}>
          <Typography variant="h4" sx={{ mb: 2, fontWeight: 600, textAlign: 'center' }}>
            Organization Details
          </Typography>

          <Typography variant="body1" sx={{ mb: 4, textAlign: 'center', color: 'text.secondary' }}>
            Please provide some basic information about your organization and AI system.
            This helps us tailor the assessment to your specific context.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          <form onSubmit={handleSubmit}>
            <TextField
              name="org_name"
              label="Organization Name"
              fullWidth
              required
              sx={{ mb: 3 }}
              value={formData.org_name}
              onChange={handleInputChange}
              disabled={loading}
            />

            <TextField
              name="contact_email"
              label="Contact Email Address"
              type="email"
              fullWidth
              required
              sx={{ mb: 3 }}
              value={formData.contact_email}
              onChange={handleInputChange}
              disabled={loading}
            />

            <TextField
              select
              name="country"
              fullWidth
              required
              label="Country"
              sx={{ mb: 4 }}
              value={formData.country}
              onChange={handleInputChange}
              disabled={loading}
            >
              {countries.map(c => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </TextField>

            {/* 3-COLUMN CSS GRID FOR COMPANY TYPE */}
            <Typography variant="h6" sx={{ mb: 1 }}>AI Company Type *</Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 2,
                mb: 4
              }}
            >
              {companyTypeInfo.map(item =>
                selectionBox(item, formData.company_type, "company_type")
              )}
            </Box>

            {/* 3-COLUMN CSS GRID FOR STAGE */}
            <Typography variant="h6" sx={{ mb: 1 }}>AI System Development Stage *</Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 2,
                mb: 4
              }}
            >
              {stageInfo.map(item =>
                selectionBox(item, formData.current_stage, "current_stage")
              )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                onClick={() => navigate('/')}
                disabled={loading}
                startIcon={<ArrowBackIcon />}
              >
                Back to Home
              </Button>

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                sx={{ minWidth: 200 }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Starting Assessment...
                  </>
                ) : "Start Assessment"}
              </Button>
            </Box>
          </form>

        </Paper>
      </Container>
    </Box>
  );
}